import useValidation from "../../hooks/useValidation";
import { useEffect, useState } from "react";
import { format, parse, isValid, isBefore } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import decisionWithValidationSchema from "../../utils/validation/decisionWithQualityValidationSchema";
import { DecisionWithQualityType } from "../../static/types/commonTypes/decisionWithQualityType";
import { AppContextType } from "../../context/AppContext";
import useAppContext from "../../hooks/useAppContext";

type decisionWithQualityProps = {
    dateLabel: string,
    decisionNatureOptions: string[],
    decisionQualityOptions: string[],
    checked: boolean,
    decisionWithQuality: DecisionWithQualityType,
    draftFieldName?: string,
    changeDecision: (decision: Partial<DecisionWithQualityType>) => void,
    onValidationChange: (isValid: boolean) => void
};

export default function DecisionWithQuality({
    checked,
    decisionWithQuality,
    decisionNatureOptions,
    decisionQualityOptions,
    dateLabel,
    draftFieldName,
    changeDecision,
    onValidationChange
}: decisionWithQualityProps) {

    const { draftFormData, setDraftFormData } = useAppContext() as AppContextType

    
    const stringifyDraft = ( data: any ) => { return JSON.stringify( data )}
    const parseDraft: any = () => { return draftFormData ? JSON.parse( draftFormData ) : {} } 

    const { errors, isValid: isDataValid } = useValidation(decisionWithQuality, decisionWithValidationSchema);
    const [dateValidationError, setDateValidationError] = useState<string>("");

    useEffect(() => {
        const validateDates = () => {
            if (decisionWithQuality.decisionDate && decisionWithQuality.effectiveDate) {
                const decisionDate = parse(decisionWithQuality.decisionDate, "dd/MM/yyyy", new Date());
                const effectiveDate = parse(decisionWithQuality.effectiveDate, "dd/MM/yyyy", new Date());
                
                if (isValid(decisionDate) && isValid(effectiveDate)) {
                    if (isBefore(effectiveDate, decisionDate)) {
                        setDateValidationError("La date d'effet ne peut pas être antérieure à la date de décision");
                        return false;
                    }
                }
            }
            setDateValidationError("");
            return true;
        };

        const areDatesValid = validateDates();
        onValidationChange(isDataValid && areDatesValid);
        
        let fieldName = draftFieldName ?? 'decisionWithQuality'
        const draft = parseDraft()
        const tempDraft = { ...draft, [ fieldName ]: decisionWithQuality }
        setDraftFormData( stringifyDraft( tempDraft ))
    }, [isDataValid, onValidationChange, decisionWithQuality]);

    useEffect(() => {
        // Initialiser avec "AG Extraordinaire" par défaut
        if (!decisionWithQuality.decisionNature) {
            const defaultNature = "AG Extraordinaire";
            changeDecision({
                ...decisionWithQuality,
                decisionNature: defaultNature,
                decisionQuality: decisionQualityOptions[0] // Initialiser aussi la qualité
            });
        } else if (!decisionWithQuality.decisionQuality && decisionNatureRequiresQuality) {
            // Si la nature existe mais pas la qualité et qu'elle est requise
            changeDecision({
                ...decisionWithQuality,
                decisionQuality: decisionQualityOptions[0]
            });
        }
    }, [decisionNatureOptions, decisionQualityOptions]);

    const [selectedDecisionDate, setSelectedDecisionDate] = useState<string>("");
    const [selectedEffectiveDate, setSelectedEffectiveDate] = useState<string>("");

    useEffect(() => {
        if (decisionWithQuality.decisionDate) {
            const parsedDate = parse(decisionWithQuality.decisionDate, "dd/MM/yyyy", new Date());
            setSelectedDecisionDate(isValid(parsedDate) ? format(parsedDate, "yyyy-MM-dd") : "");
        }
        if (decisionWithQuality.effectiveDate) {
            const parsedDate = parse(decisionWithQuality.effectiveDate, "dd/MM/yyyy", new Date());
            setSelectedEffectiveDate(isValid(parsedDate) ? format(parsedDate, "yyyy-MM-dd") : "");
        }
    }, [decisionWithQuality]);

    const handleDecisionDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputDate = e.target.value;
        const parsedDate = parse(inputDate, "yyyy-MM-dd", new Date());
        const formattedDate = format(parsedDate, "dd/MM/yyyy");

        setSelectedDecisionDate(inputDate);
        changeDecision({ decisionDate: isValid(parsedDate) ? formattedDate : "" });
    };

    const handleEffectiveDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputDate = e.target.value;
        const parsedDate = parse(inputDate, "yyyy-MM-dd", new Date());
        const formattedDate = format(parsedDate, "dd/MM/yyyy");

        setSelectedEffectiveDate(inputDate);
        changeDecision({ effectiveDate: isValid(parsedDate) ? formattedDate : "" });
    };

    const openDatePicker = (id: string) => {
        const input = document.getElementById(id) as HTMLInputElement;
        input?.showPicker();
    };

    const decisionNatureRequiresQuality = ["AG Extraordinaire", "AG Ordinaire", "AG Mixte"].includes(decisionWithQuality.decisionNature);

    return (
        <div className="tab-content p-4 shadows">
            <div className="card-body ">
                <div className="mb-4 row align-items-center">
                    <label
                        htmlFor="decisionNature"
                        className="col-sm-3 col-form-label fw-bold"
                    >
                        Org. de décision <strong className="text-danger">*</strong>
                    </label>
                    <div className="col-sm-6">
                        <select
                            className={`form-select ${
                                checked && errors.decisionNature ? "is-invalid" : ""
                            }`}
                            id="decisionNature"
                            onChange={(event) =>
                                changeDecision({ decisionNature: event.target.value })
                            }
                            value={decisionWithQuality.decisionNature || "AG Extraordinaire"}
                        >
                            {decisionNatureOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                        {checked && errors.decisionNature && (
                            <div className="invalid-feedback">
                                {errors.decisionNature}
                            </div>
                        )}
                    </div>
                </div>
                
                {decisionNatureRequiresQuality && (
                    <div className="mb-4 row align-items-center">
                        <label
                            htmlFor="decisionWithQuality"
                            className="col-sm-3 col-form-label fw-bold"
                        >
                            Qualité de l'org.{" "}
                            <strong className="text-danger">*</strong>
                        </label>
                        <div className="col-sm-6">
                            <select
                                className={`form-select ${
                                    checked && errors.decisionQuality
                                        ? "is-invalid"
                                        : ""
                                }`}
                                id="decisionWithQuality"
                                onChange={(event) =>
                                    changeDecision({
                                        decisionQuality: event.target.value,
                                    })
                                }
                                value={decisionWithQuality.decisionQuality || decisionQualityOptions[0]}
                            >
                                {decisionQualityOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                            {checked && errors.decisionQuality && (
                                <div className="invalid-feedback">
                                    {errors.decisionQuality}
                                </div>
                            )}
                        </div>
                    </div>
                )}
                
                <div className="mb-4 row align-items-center">
                    <label
                        htmlFor="decisionDate"
                        className="col-sm-3 col-form-label fw-bold mb-3"
                    >
                        {dateLabel} <strong className="text-danger">*</strong>
                    </label>
                    <div className="col-sm-6">
                        <input
                            className={`form-control ${
                                checked && errors.decisionDate ? "is-invalid" : ""
                            }`}
                            type="date"
                            id="decisionDate"
                            value={selectedDecisionDate}
                            onChange={handleDecisionDateChange}
                            onClick={() => openDatePicker("decisionDate")}
                            placeholder="JJ/MM/AAAA"
                        />
                        {checked && errors.decisionDate && (
                            <div className="invalid-feedback">
                                {errors.decisionDate}
                            </div>
                        )}
                    </div>
                </div>

                <div className="mb-4 row align-items-center">
                    <label
                        htmlFor="effectiveDate"
                        className="col-sm-3 col-form-label fw-bold mb-3"
                    >
                        Date d'effet <strong className="text-danger">*</strong>
                    </label>
                    <div className="col-sm-6">
                        <input
                            className={`form-control ${(checked && errors.effectiveDate) || (checked && dateValidationError) ? "is-invalid" : ""}`}
                            type="date"
                            id="effectiveDate"
                            value={selectedEffectiveDate}
                            onChange={handleEffectiveDateChange}
                            onClick={() => openDatePicker("effectiveDate")}
                            placeholder="JJ/MM/AAAA"
                            required
                        />
                        {checked && errors.effectiveDate && (
                            <div className="invalid-feedback">
                                {errors.effectiveDate}
                            </div>
                        )}
                        {checked && dateValidationError && !errors.effectiveDate && (
                            <div className="invalid-feedback">
                                {dateValidationError}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}